import { Button } from '@tvg/fuse-design/buttons';
import { Drawer as FuseDrawer } from '@tvg/fuse-design/drawer';
import IconChevronRight from '@tvg/fuse-icons/lib/ChevronRight';
import { useDrawerState } from 'client/state/Drawer';
import React, { useEffect, useMemo, useRef } from 'react';
import usePortal from 'react-useportal';
import styled from 'styled-components';
import useSSR from 'use-ssr';

const FuseDrawerStyled = styled.div`
  & .fuse-drawer .fuse-drawer__body {
    flex: 1;
    padding-top: var(--spacing-space24);
  }
  & .drawer-inner__sticky-drawer__hide-shadow {
    box-shadow: none;
  }
`;

export type DrawerInnerProps = {
  name: string;
  open: boolean;
  onCloseHandler: () => void;
  onDrawerBack: () => void;
  children?: React.ReactNode;
};

function DrawerInner({ name, open, onCloseHandler, onDrawerBack, children }: DrawerInnerProps) {
  const showViewProducts = useDrawerState((s) => s.showViewProducts);
  const toggleDrawerOpen = useDrawerState((s) => s.toggleDrawerOpen);
  const drawerRef = useRef<HTMLElement>(null);
  const { isBrowser } = useSSR();

  const { Portal } = usePortal({
    bindTo: isBrowser ? document.getElementById('drawer--inner') || undefined : undefined,
  });

  useEffect(() => {
    if (open) {
      drawerRef.current?.focus();
      setTimeout(() => {
        toggleDrawerOpen.on();
      }, 100);
    }
  }, [open, toggleDrawerOpen]);

  const stickyDrawer = useMemo(
    () => ({
      container: {
        className: !open ? 'drawer-inner__sticky-drawer__hide-shadow' : '',
        dataTestId: 'drawer-inner-sticky-drawer-data-testid',
        open: showViewProducts,
        transition: '200ms',
        zIndex: '1',
      },
      button: (
        <Button
          dataTestId="drawer-inner-sticky-drawer-button-data-testid"
          fullWidth
          icon={<IconChevronRight />}
          iconRight
          size="large"
          variant="primary"
          onClick={onCloseHandler}
        >
          View Products
        </Button>
      ),
    }),
    [open, onCloseHandler, showViewProducts],
  );

  return (
    <Portal>
      <FuseDrawerStyled aria-hidden={!open}>
        <FuseDrawer
          srLabelBackButton="Back to filters"
          srLabelCloseButton={`Close ${name} list`}
          className="modal"
          hasBackButton
          open={open}
          onClose={onCloseHandler}
          title={name}
          backCallback={onDrawerBack}
          zIndex="21"
          ref={drawerRef}
          dataTestId="facet-item-drawer--inner"
          stickyDrawer={stickyDrawer}
        >
          {children}
        </FuseDrawer>
      </FuseDrawerStyled>
    </Portal>
  );
}

export default React.memo(DrawerInner);
